import React, { useEffect } from 'react';

const FacadePattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Facade Pattern - Structural Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Facade Pattern - provide simplified interface to complex subsystems. Learn with home theater and payment processing examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Facade Pattern - provide simplified interface to complex subsystems. Learn with home theater and payment processing examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'facade pattern, design patterns, structural patterns, simplified interface, subsystem, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'facade pattern, design patterns, structural patterns, simplified interface, subsystem, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Facade Pattern</h1>
        <p style={styles.subtitle}>
          Structural Design Pattern - Provide simplified interface to complex subsystems
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Facade pattern provides a simplified interface to a complex subsystem. It defines 
            a higher-level interface that makes the subsystem easier to use by hiding the 
            complexities of the underlying components.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to work with a complex subsystem that has many interdependent classes and 
            complicated initialization procedures. You want to provide a simple interface that 
            handles the complexity for common use cases.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Home Theater System</h2>
          <div style={styles.codeBlock}>
{`// Complex subsystem components
class Amplifier {
    public void on() { System.out.println("Amplifier on"); }
    public void off() { System.out.println("Amplifier off"); }
    public void setVolume(int volume) { System.out.println("Setting volume to " + volume); }
}

class DVDPlayer {
    public void on() { System.out.println("DVD Player on"); }
    public void off() { System.out.println("DVD Player off"); }
    public void play(String movie) { System.out.println("Playing: " + movie); }
    public void stop() { System.out.println("Stopping DVD"); }
}

class Projector {
    public void on() { System.out.println("Projector on"); }
    public void off() { System.out.println("Projector off"); }
    public void setInput(String input) { System.out.println("Setting input to " + input); }
}

class Screen {
    public void down() { System.out.println("Screen coming down"); }
    public void up() { System.out.println("Screen going up"); }
}

class PopcornPopper {
    public void on() { System.out.println("Popcorn maker on"); }
    public void off() { System.out.println("Popcorn maker off"); }
    public void pop() { System.out.println("Popping popcorn!"); }
}

class TheaterLights {
    public void on() { System.out.println("Theater lights on"); }
    public void off() { System.out.println("Theater lights off"); }
    public void dim(int level) { System.out.println("Dimming lights to " + level + "%"); }
}

// Facade - provides simplified interface
class HomeTheaterFacade {
    private Amplifier amplifier;
    private DVDPlayer dvdPlayer;
    private Projector projector;
    private Screen screen;
    private PopcornPopper popper;
    private TheaterLights lights;
    
    public HomeTheaterFacade(Amplifier amplifier, DVDPlayer dvdPlayer, 
                           Projector projector, Screen screen, 
                           PopcornPopper popper, TheaterLights lights) {
        this.amplifier = amplifier;
        this.dvdPlayer = dvdPlayer;
        this.projector = projector;
        this.screen = screen;
        this.popper = popper;
        this.lights = lights;
    }
    
    public void watchMovie(String movie) {
        System.out.println("Get ready to watch a movie...");
        popper.on();
        popper.pop();
        lights.dim(10);
        screen.down();
        projector.on();
        projector.setInput("DVD");
        amplifier.on();
        amplifier.setVolume(5);
        dvdPlayer.on();
        dvdPlayer.play(movie);
        System.out.println("Movie started! Enjoy!");
    }
    
    public void endMovie() {
        System.out.println("Shutting movie theater down...");
        popper.off();
        lights.on();
        screen.up();
        projector.off();
        amplifier.off();
        dvdPlayer.stop();
        dvdPlayer.off();
        System.out.println("Movie theater is off.");
    }
}

// Usage
public class FacadePatternDemo {
    public static void main(String[] args) {
        // Create subsystem components
        Amplifier amplifier = new Amplifier();
        DVDPlayer dvdPlayer = new DVDPlayer();
        Projector projector = new Projector();
        Screen screen = new Screen();
        PopcornPopper popper = new PopcornPopper();
        TheaterLights lights = new TheaterLights();
        
        // Create facade
        HomeTheaterFacade homeTheater = new HomeTheaterFacade(
            amplifier, dvdPlayer, projector, screen, popper, lights);
        
        // Simple interface to complex operations
        homeTheater.watchMovie("The Matrix");
        System.out.println();
        homeTheater.endMovie();
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Simplicity:</strong> Provides easy-to-use interface for complex systems</li>
            <li style={styles.listItem}><strong>Decoupling:</strong> Isolates clients from subsystem complexity</li>
            <li style={styles.listItem}><strong>Layered Architecture:</strong> Promotes clean separation of concerns</li>
            <li style={styles.listItem}><strong>Flexibility:</strong> Doesn't prevent access to underlying subsystem</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need to provide simple interface to complex subsystem</li>
            <li style={styles.listItem}>When you want to decouple clients from subsystem components</li>
            <li style={styles.listItem}>When you need to layer your subsystems</li>
            <li style={styles.listItem}>When you want to wrap legacy or third-party code</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Facade pattern is essential for managing complexity in large systems. It provides 
            a clean, simple interface while preserving the flexibility to access underlying 
            components when needed. This makes systems easier to use and maintain.
          </p>
        </section>
      </main>
    </article>
  );
};

export default FacadePattern;
